import { ArrowForward, OpenInNew, SwapHoriz } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
} from '@mui/material';
import { FC, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { CardTitleWithEdit } from '../../../components/cardTitleWithEdit/CardTitleWithEdit';
import { entityToDTO } from '../../../components/editUtils';
import { formatKundeAdresse } from '../../../components/formatUtils';
import { formatDate } from '../../../components/formatUtils/dateFormatUtils';
import { useNavigateDialogForm } from '../../../components/formikDialogForm/dialogForm.hook';
import { PlainEntityDetailsHeader } from '../../../components/plainEntityDetailsHeader/PlainEntityDetailsHeader';
import { TextDisplay } from '../../../components/TextDisplay/TextDisplay';
import { useMitarbeiterRole } from '../../auth/api/auth.hook';
import { MitarbeiterRole } from '../../auth/api/types';
import { VisibleIfAdmin } from '../../auth/components/VisibleIfAdmin';
import {
  useConvertInteressentToKundeMutation,
  useFindKundeByIdQuery,
} from '../../kunde/api/kundeApi';
import { getInteressentenStatusLabel } from '../../kunde/api/types';

export const InteressentViewScreen: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useMitarbeiterRole();
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const { data: interessent, isLoading } = useFindKundeByIdQuery(id as string);
  const [convertInteressentToKunde, { isLoading: isConverting }] =
    useConvertInteressentToKundeMutation();
  const navigateDialog = useNavigateDialogForm(
    'interessent',
    'edit',
    interessent ? entityToDTO(interessent) : undefined
  );

  const convertToKunde = async () => {
    if (!id || !interessent || interessent.typ !== 'INTERESSENT') {
      return;
    }

    try {
      const kunde = await convertInteressentToKunde(id).unwrap();
      toast.success('Interessent wurde zu einem Kunden umgewandelt.');
      navigate(`/intern/kunde/${kunde.id}`);
    } catch {
      toast.error('Interessent konnte nicht umgewandelt werden.');
    }
  };

  const closeConvertDialog = () => {
    if (!isConverting) {
      setIsConvertDialogOpen(false);
    }
  };

  const confirmConversion = async () => {
    await convertToKunde();
    setIsConvertDialogOpen(false);
  };

  return (
    <Box>
      {isLoading && <LinearProgress sx={{ width: '100%' }} />}
      {interessent && (
        <>
          <PlainEntityDetailsHeader
            title={interessent.organisation}
            createdAt={interessent.creationDate}
            updatedAt={interessent.updateDate}
          />
          <Stack spacing={3} sx={{ width: { xs: '100%', md: '65%' } }}>
            <Card>
              <CardContent>
                <CardTitleWithEdit
                  title="Stammdaten"
                  onClickEdit={role === MitarbeiterRole.ADMIN ? navigateDialog : undefined}
                />
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                    <TextDisplay label="Organisation" value={interessent.organisation} />
                    <TextDisplay
                      label="Operativer Ansprechpartner"
                      value={interessent.operativerAnsprechpartner}
                    />
                  </Stack>
                  <TextDisplay label="Adresse" value={formatKundeAdresse(interessent)} />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                    <TextDisplay label="E-Mail" value={interessent.email} />
                    <TextDisplay label="Telefonnummer" value={interessent.telefonnummer} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                    <TextDisplay
                      label="Status"
                      value={getInteressentenStatusLabel(interessent.vertriebsStatus)}
                    />
                    <TextDisplay label="Angebotsnummer" value={interessent.angebotsnummer} />
                    <TextDisplay label="Quelle" value={interessent.quelle} />
                  </Stack>
                  <TextDisplay
                    label="Angebotslink"
                    value={
                      interessent.angebotslink ? (
                        <Link
                          href={interessent.angebotslink}
                          target="_blank"
                          rel="noreferrer"
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                          Angebot öffnen <OpenInNew fontSize="inherit" />
                        </Link>
                      ) : null
                    }
                  />
                  <TextDisplay
                    label="Zuletzt bearbeitet"
                    value={
                      interessent.zuletztBearbeitet
                        ? formatDate(interessent.zuletztBearbeitet)
                        : null
                    }
                  />
                  <TextDisplay label="Bemerkung" value={interessent.bemerkung} />
                </Stack>
              </CardContent>
            </Card>
            <VisibleIfAdmin>
              <Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SwapHoriz />}
                  endIcon={<ArrowForward />}
                  onClick={() => setIsConvertDialogOpen(true)}
                >
                  Zu Kunde umwandeln
                </Button>
              </Box>
            </VisibleIfAdmin>
          </Stack>
          <Dialog open={isConvertDialogOpen} onClose={closeConvertDialog}>
            <DialogTitle>Interessent zu Kunde umwandeln?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Der Interessent wird als Kunde geführt und erscheint anschließend nicht mehr in der
                Interessentenliste.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeConvertDialog} disabled={isConverting}>
                Abbrechen
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={confirmConversion}
                disabled={isConverting}
              >
                Zu Kunde umwandeln
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};
